import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../hooks/useTranslation';

const Container = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  padding: 20px;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    padding: 40px;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 300;
  background: var(--gradient-ocean);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: var(--gray);
  font-size: 0.875rem;
`;

const InputCard = styled.div`
  background: rgba(26, 26, 26, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
`;

const InputLabel = styled.label`
  display: block;
  color: var(--light-gray);
  font-size: 0.875rem;
  margin-bottom: 8px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px;
  color: white;
  font-family: var(--font-sans);
  font-size: 1rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--kuutio-blue);
    box-shadow: 0 0 0 2px rgba(0, 153, 255, 0.2);
  }
  
  &::placeholder {
    color: var(--gray);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.primary ? 'var(--gradient-ocean)' : 'transparent'};
  border: ${props => props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px ${props => props.primary ? 'rgba(0, 153, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const TypeSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TypeChip = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? props.color : 'transparent'};
  border: 1px solid ${props => props.color};
  border-radius: 20px;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.color};
    transform: scale(1.05);
  }
`;

const RecentInputs = styled.div`
  margin-top: 32px;
`;

const RecentTitle = styled.h3`
  color: var(--white);
  font-size: 1.25rem;
  margin-bottom: 16px;
`;

const InputItem = styled.div`
  background: rgba(26, 26, 26, 0.6);
  border-left: 3px solid ${props => props.color};
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const InputText = styled.p`
  color: var(--light-gray);
  font-size: 0.875rem;
  margin-bottom: 4px;
`;

const InputMeta = styled.div`
  color: var(--gray);
  font-size: 0.75rem;
`;

function TabletInterface() {
  const { t, language } = useTranslation();
  const [inputText, setInputText] = useState('');
  const [selectedType, setSelectedType] = useState('idea');
  const [recentInputs, setRecentInputs] = useState([]);

  const inputTypes = [
    { value: 'idea', label: t('tablet.typeIdea'), color: 'var(--kuutio-blue)' },
    { value: 'concern', label: t('tablet.typeConcern'), color: 'var(--kuutio-pink)' },
    { value: 'question', label: t('tablet.typeQuestion'), color: 'var(--kuutio-yellow)' },
    { value: 'scenario', label: t('tablet.typeScenario'), color: 'var(--kuutio-green)' }
  ];

  const handleSubmit = () => {
    if (!inputText.trim()) return;

    const newInput = {
      id: Date.now(),
      text: inputText,
      type: selectedType,
      color: inputTypes.find(type => type.value === selectedType)?.color,
      timestamp: new Date().toLocaleTimeString(
        language === 'en' ? 'en-GB' : language === 'sv' ? 'sv-SE' : 'fi-FI'
      )
    };

    setRecentInputs(prev => [newInput, ...prev].slice(0, 5));
    setInputText('');
    
    // Here you would send to WebSocket
    console.log('Sending to server:', newInput);
  };

  const handleClear = () => {
    setInputText('');
  };

  return (
    <Container>
      <Header>
        <Title>KUUTIO</Title>
        <Subtitle>{t('tablet.subtitle')}</Subtitle>
      </Header>

      <InputCard>
        <InputLabel>{t('tablet.selectType')}</InputLabel>
        <TypeSelector>
          {inputTypes.map(type => (
            <TypeChip
              key={type.value}
              active={selectedType === type.value}
              color={type.color}
              onClick={() => setSelectedType(type.value)}
            >
              {type.label}
            </TypeChip>
          ))}
        </TypeSelector>

        <InputLabel>{t('tablet.inputLabel')}</InputLabel>
        <TextArea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={t('tablet.placeholder')}
          maxLength={500}
        />
        
        <ButtonGroup>
          <Button onClick={handleClear}>
            {t('tablet.clear')}
          </Button>
          <Button primary onClick={handleSubmit}>
            {t('tablet.send')}
          </Button>
        </ButtonGroup>
      </InputCard>

      {recentInputs.length > 0 && (
        <RecentInputs>
          <RecentTitle>{t('tablet.ownInputs')}</RecentTitle>
          {recentInputs.map(input => (
            <InputItem key={input.id} color={input.color}>
              <InputText>{input.text}</InputText>
              <InputMeta>
                {inputTypes.find(type => type.value === input.type)?.label} • {input.timestamp}
              </InputMeta>
            </InputItem>
          ))}
        </RecentInputs>
      )}
    </Container>
  );
}

export default TabletInterface;
